import { useQuery as useApolloQuery } from '@apollo/client';
import { OperationVariables } from '@apollo/client/core';
import {
    QueryHookOptions,
    QueryResult,
} from '@apollo/client/react/types/types';
import { DocumentNode } from 'graphql';
import { useMemo } from 'react';

export interface DocumentTypeDecoration<TResult, TVariables> {
    /**
     * This type is used to ensure that the variables you pass in to the query are assignable to Variables
     * and that the Result is assignable to whatever you pass your result to. The method is never actually
     * implemented, but the type is valid because we list it as optional
     */
    __apiType?: (variables: TVariables) => TResult;
}

export interface TypedDocumentNode<
    TResult = {
        [key: string]: any;
    },
    TVariables = {
        [key: string]: any;
    }
> extends DocumentNode, DocumentTypeDecoration<TResult, TVariables> {}

/**
 * Modern cached query hook using Apollo Client's built-in optimizations
 * Replaces deprecated QueryData with modern Apollo patterns for better performance
 */
export function useCachedQuery<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables
>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
    const optimizedOptions = useMemo(() => {
        const baseOptions: QueryHookOptions<TData, TVariables> = {
            fetchPolicy: options?.fetchPolicy || 'cache-first',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: false,
            ...(options?.context?.requiresFreshGenNo && {
                fetchPolicy: 'network-only',
            }),
            context: {
                ...options?.context,
            },
            ...options,
        };

        return baseOptions;
    }, [
        options?.variables,
        options?.fetchPolicy,
        options?.errorPolicy,
        options?.skip,
        options?.pollInterval,
        options?.context,
    ]);

    const result = useApolloQuery<TData, TVariables>(query, optimizedOptions);

    return result;
}
